import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AcademicYearModal = ({ isOpen, onClose, academicYear, onSave, mode }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    status: 'upcoming'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (academicYear) {
      setFormData({
        name: academicYear.name || '',
        start_date: academicYear.start_date || '',
        end_date: academicYear.end_date || '',
        status: academicYear.status || 'upcoming'
      });
    } else {
      // For new year, generate next year automatically
      const currentYear = new Date().getFullYear();
      setFormData({
        name: `${currentYear + 1}-${(currentYear + 2).toString().slice(2)}`,
        start_date: '',
        end_date: '',
        status: 'upcoming'
      });
    }
    setErrors({});
  }, [academicYear, mode, isOpen]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Academic year label is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      // onClose handled by parent on success
    } catch (error) {
      console.error('Error saving academic year:', error);
      setErrors({ submit: 'Failed to save academic year. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (!academicYear) return 'Add New Academic Year';
    return 'Edit Academic Year';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full shadow-lg">
        <div className="bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Academic Year Label *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., 2025-26"
                className={errors.name ? 'border-error' : ''}
              />
              {errors.name && (
                <p className="text-sm text-error mt-1">{errors.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors.start_date ? 'border-error' : 'border-border'
                    }`}
                />
                {errors.start_date && (
                  <p className="text-sm text-error mt-1">{errors.start_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors.end_date ? 'border-error' : 'border-border'
                    }`}
                />
                {errors.end_date && (
                  <p className="text-sm text-error mt-1">{errors.end_date}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1 text-sm text-foreground">
                <p className="font-medium mb-1">Academic Year Guidelines:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Academic year should span approximately 10-12 months</li>
                  <li>Only one academic year and one semester should be Active at a time</li>
                  <li>Setting a year/semester to 'Active' will automatically close others</li>
                </ul>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="bg-error/10 border border-error rounded-lg p-3">
              <p className="text-sm text-error">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  {academicYear ? 'Save Changes' : 'Create Year'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcademicYearModal;